import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, CalendarClock, CheckCircle2, Loader2, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const URG = {
  depasse: { label: "Dépassé", cls: "bg-red-600 text-white border-red-600" },
  critique: { label: "Critique", cls: "bg-red-100 text-red-700 border-red-200" },
  urgent: { label: "Urgent", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  attention: { label: "Attention", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  ok: { label: "OK", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const scoreColor = (s) => (s >= 70 ? "#059669" : s >= 40 ? "#d97706" : "#dc2626");

const Gauge = ({ score, grade }) => {
  const c = 2 * Math.PI * 52;
  return (
    <div className="relative w-36 h-36 shrink-0" data-testid="hub-score">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r="52" fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <circle cx="60" cy="60" r="52" fill="none" stroke={scoreColor(score)} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold font-mono" style={{ color: scoreColor(score) }}>{score}</span>
        <span className="text-[11px] uppercase tracking-wide text-slate-500">/100 · note {grade}</span>
      </div>
    </div>
  );
};

const Priorities = ({ items, onNavigate, onSync, busy }) => (
  <Card data-testid="hub-priorities">
    <CardHeader className="pb-2"><CardTitle className="text-base">À faire en priorité</CardTitle></CardHeader>
    <CardContent className="space-y-2">
      {items.length === 0 && <p className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Tout est en ordre.</p>}
      {items.map((p, i) => (
        <div key={i} className="flex items-center gap-3 p-2.5 rounded-md border bg-white hover:bg-slate-50 transition-colors" data-testid={`hub-priority-${i}`}>
          <Badge variant="outline" className={`${URG[p.urgence]?.cls} text-[10px] w-20 justify-center`}>{URG[p.urgence]?.label}</Badge>
          <span className="flex-1 text-sm">{p.label}</span>
          {p.points > 0 && <span className="text-xs text-slate-400 font-mono">+{p.points} pts</span>}
          {p.action === "sync" ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onSync(p.register)} data-testid={`hub-priority-sync-${i}`}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" />Synchroniser</Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => onNavigate(p.tab)} data-testid={`hub-priority-go-${i}`}>
              Y aller<ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
          )}
        </div>
      ))}
    </CardContent>
  </Card>
);

const Timeline = ({ items, onNavigate }) => (
  <Card data-testid="hub-timeline">
    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-4 h-4" />Échéances légales</CardTitle></CardHeader>
    <CardContent>
      {items.length === 0 && <p className="text-sm text-slate-500">Renseigne la date de clôture d'exercice (onglet Profil) pour calculer tes échéances.</p>}
      <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
        {items.map((t, i) => (
          <li key={i} className="pl-5 relative" data-testid={`hub-deadline-${i}`}>
            <span className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full border-2 border-white ${t.urgence === "ok" ? "bg-emerald-500" : t.urgence === "attention" ? "bg-amber-500" : "bg-red-500"}`} />
            <button onClick={() => onNavigate(t.tab)} className="text-left w-full group">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-slate-500">{fmt(t.date)}</span>
                <Badge variant="outline" className={`${URG[t.urgence]?.cls} text-[10px]`}>
                  {t.days_remaining < 0 ? `dépassé de ${-t.days_remaining} j` : `J-${t.days_remaining}`}</Badge>
              </div>
              <p className="text-sm font-medium group-hover:text-blue-700">{t.label}</p>
              {t.detail && <p className="text-xs text-slate-500">{t.detail}</p>}
            </button>
          </li>
        ))}
      </ol>
    </CardContent>
  </Card>
);

export const LegalHub = ({ onNavigate, refreshKey, onChanged }) => {
  const [hub, setHub] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/legal/hub").then((r) => setHub(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [refreshKey]);

  const sync = async (type) => {
    setBusy(true);
    try {
      const r = await api.post(`/legal/registers/${type}/sync`);
      toast.success(`${r.data.created} inscription(s) ajoutée(s) au registre`);
      load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!hub) return <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Analyse du dossier…</div>;
  return (
    <div className="space-y-4" data-testid="legal-hub">
      <Card>
        <CardContent className="p-5 flex flex-col md:flex-row gap-6 items-center">
          <Gauge score={hub.score} grade={hub.grade} />
          <div className="flex-1 w-full space-y-2.5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="font-semibold">Conformité juridique · {hub.forme_juridique}{hub.exercice ? ` · exercice ${hub.exercice}` : ""}</p>
              <span className="text-xs text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" />{hub.encrypted_documents}/{hub.documents_count} documents chiffrés</span>
            </div>
            {hub.sections.map((s) => (
              <div key={s.key} data-testid={`hub-section-${s.key}`}>
                <div className="flex justify-between text-xs mb-1"><span>{s.label}</span><span className="font-mono">{s.points}/{s.max}</span></div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(s.points / s.max) * 100}%`, background: scoreColor((s.points / s.max) * 100) }} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3"><Priorities items={hub.priorities} onNavigate={onNavigate} onSync={sync} busy={busy} /></div>
        <div className="lg:col-span-2"><Timeline items={hub.timeline} onNavigate={onNavigate} /></div>
      </div>
    </div>
  );
};
